/**
 * Print Templates.
 *
 * Generates HTML strings for print-ready documents styled to match
 * the application's Apex Design System. Each template includes:
 * - Professional document header with company info and brand accent
 * - Counterparty (vendor/customer) block
 * - Document metadata (number, dates, status)
 * - Structured line items table
 * - Financial summary with totals
 * - Notes section
 *
 * Used by the PrintButton component.
 */

const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    v,
  );

const fmtDate = (d: string | null | undefined) =>
  d
    ? new Date(d).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

/** Status badge CSS class. */
function statusClass(status: string): string {
  const s = status.toLowerCase();
  if (["paid", "received", "completed", "accepted"].includes(s))
    return "status-success";
  if (
    ["submitted", "in-transit", "in progress", "confirmed", "sent"].includes(s)
  )
    return "status-warning";
  if (["cancelled", "declined", "overdue"].includes(s)) return "status-danger";
  if (["open", "draft"].includes(s)) return "status-info";
  return "status-default";
}

/** Shared company info structure. */
interface OrgInfo {
  company_name?: string;
  phone?: string;
  email?: string;
  website?: string;
  primary_address?: {
    street1?: string;
    street2?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
}

/** Render the document header block. */
function renderDocHeader(
  org: OrgInfo | undefined,
  docType: string,
  docNumber: string,
  status: string,
  meta: Record<string, string | null | undefined>,
  party?: { label: string; name: string; detail?: string },
): string {
  const name = org?.company_name || "TradeRoute";
  const addr = org?.primary_address;
  const contactParts = [org?.phone, org?.email, org?.website]
    .filter(Boolean)
    .join(" · ");

  const metaLines = Object.entries(meta)
    .filter(([, v]) => v)
    .map(([k, v]) => `<p>${k}: ${v}</p>`)
    .join("");

  return `
    <div class="doc-header">
      <div class="doc-header-accent"></div>
      <div class="doc-header-body">
        <div class="doc-header-row">
          <div>
            <div class="doc-company-name">${name}</div>
            ${addr?.street1 ? `<div class="doc-company-detail">${addr.street1}${addr.street2 ? `, ${addr.street2}` : ""}</div>` : ""}
            ${addr?.city ? `<div class="doc-company-detail">${addr.city}, ${addr.state || ""} ${addr.zip || ""}</div>` : ""}
            ${contactParts ? `<div class="doc-company-detail">${contactParts}</div>` : ""}
          </div>
          <div>
            <div class="doc-type-label">${docType}</div>
            <div class="doc-number">${docNumber}</div>
            <div class="doc-meta">
              ${metaLines}
              <span class="doc-status ${statusClass(status)}">${status}</span>
            </div>
          </div>
        </div>
        ${
          party
            ? `
        <div class="doc-party">
          <div class="doc-party-label">${party.label}</div>
          <div class="doc-party-name">${party.name}</div>
          ${party.detail ? `<div class="doc-party-detail">${party.detail}</div>` : ""}
        </div>`
            : ""
        }
      </div>
    </div>
  `;
}

// ==========================================================================
// Purchase Order
// ==========================================================================

export function generatePOHtml(
  po: {
    po_number: string;
    status: string;
    order_date: string | null;
    expected_delivery_date?: string | null;
    vendor_name?: string | null;
    currency?: string;
    subtotal: number;
    shipping_cost: number;
    tax: number;
    total: number;
    notes?: string | null;
    line_items: {
      description: string;
      asset_name: string | null;
      category: string;
      quantity: number;
      unit_price: number;
      total_price: number;
    }[];
  },
  org?: OrgInfo,
): string {
  const header = renderDocHeader(
    org,
    "Purchase Order",
    po.po_number,
    po.status,
    {
      Date: fmtDate(po.order_date),
      "Expected Delivery": fmtDate(po.expected_delivery_date),
      Currency: po.currency && po.currency !== "USD" ? po.currency : undefined,
    },
    po.vendor_name ? { label: "Vendor", name: po.vendor_name } : undefined,
  );

  const lineRows = po.line_items
    .map(
      (l, i) => `
    <tr>
      <td class="text-center text-muted">${i + 1}</td>
      <td>${l.asset_name || l.description}</td>
      <td class="capitalize">${l.category}</td>
      <td class="text-center">${l.quantity}</td>
      <td class="text-right">${fmt(l.unit_price)}</td>
      <td class="text-right font-bold">${fmt(l.total_price)}</td>
    </tr>`,
    )
    .join("");

  // Only the scalar tax total is printed below. The tax scheme name, applied
  // rate, and any manual-override detail are deliberately kept out of the
  // printed/emailed order — they live only in the in-app Tax drawer.
  return `
    ${header}

    <div class="section">
      <div class="section-title">Items</div>
      <table>
        <thead><tr>
          <th class="text-center" style="width:40px">#</th>
          <th>Description</th>
          <th>Category</th>
          <th class="text-center">Qty</th>
          <th class="text-right">Unit Price</th>
          <th class="text-right">Total</th>
        </tr></thead>
        <tbody>${lineRows}</tbody>
      </table>
    </div>

    <table class="totals-table">
      <tbody>
        <tr><td>Subtotal</td><td class="text-right">${fmt(po.subtotal)}</td></tr>
        <tr><td>Shipping</td><td class="text-right">${fmt(po.shipping_cost)}</td></tr>
        <tr><td>Tax</td><td class="text-right">${fmt(po.tax)}</td></tr>
        <tr class="total-row"><td>Total</td><td class="text-right text-brand">${fmt(po.total)}</td></tr>
      </tbody>
    </table>

    ${po.notes ? `<div class="section"><div class="section-title">Notes</div><div class="notes">${po.notes}</div></div>` : ""}
  `;
}

// ==========================================================================
// Work Order — Job Sheet
// ==========================================================================

export function generateWOHtml(
  wo: {
    wo_number: string;
    title: string;
    status: string;
    work_type: string;
    priority: string;
    description: string | null;
    estimated_hours: number | null;
    actual_hours: number;
    started_at?: string | null;
    due_date?: string | null;
    total_labor_cost: number;
    total_parts_cost: number;
    total_materials_cost: number;
    total_cost: number;
    labor_entries: {
      description: string | null;
      hours: number;
      hourly_rate: number;
      total_cost: number;
    }[];
    parts_entries: {
      part_name: string;
      part_number: string | null;
      quantity: number;
      unit_cost: number;
      total_cost: number;
    }[];
    materials_entries?: {
      material_name: string;
      quantity: number;
      unit: string;
      unit_cost: number;
      total_cost: number;
    }[];
  },
  org?: OrgInfo,
): string {
  const header = renderDocHeader(org, "Work Order", wo.wo_number, wo.status, {
    Type: wo.work_type,
    Priority: wo.priority,
    Started: fmtDate(wo.started_at),
    Due: fmtDate(wo.due_date),
  });

  const laborRows = wo.labor_entries
    .map(
      (l) => `
    <tr>
      <td>${l.description || "—"}</td>
      <td class="text-right">${l.hours}h</td>
      <td class="text-right">${fmt(l.hourly_rate)}</td>
      <td class="text-right font-bold">${fmt(l.total_cost)}</td>
    </tr>`,
    )
    .join("");

  const partsRows = wo.parts_entries
    .map(
      (p) => `
    <tr>
      <td>${p.part_name}</td>
      <td class="font-mono">${p.part_number || "—"}</td>
      <td class="text-center">${p.quantity}</td>
      <td class="text-right">${fmt(p.unit_cost)}</td>
      <td class="text-right font-bold">${fmt(p.total_cost)}</td>
    </tr>`,
    )
    .join("");

  const materialsRows = (wo.materials_entries || [])
    .map(
      (m) => `
    <tr>
      <td>${m.material_name}</td>
      <td class="text-center">${m.quantity} ${m.unit}</td>
      <td class="text-right">${fmt(m.unit_cost)}</td>
      <td class="text-right font-bold">${fmt(m.total_cost)}</td>
    </tr>`,
    )
    .join("");

  return `
    ${header}

    <div class="section">
      <div class="section-title">${wo.title}</div>
      <div class="section-body">
        ${wo.description ? `<p style="color:#64607a;margin-bottom:8px;">${wo.description}</p>` : ""}
        <p>Estimated: <strong>${wo.estimated_hours ?? "—"}</strong> hrs &middot; Actual: <strong>${wo.actual_hours}</strong> hrs</p>
      </div>
    </div>

    ${
      wo.labor_entries.length > 0
        ? `
    <div class="section">
      <div class="section-title">Labor</div>
      <table>
        <thead><tr><th>Description</th><th class="text-right">Hours</th><th class="text-right">Rate</th><th class="text-right">Total</th></tr></thead>
        <tbody>${laborRows}</tbody>
      </table>
    </div>`
        : ""
    }

    ${
      wo.parts_entries.length > 0
        ? `
    <div class="section">
      <div class="section-title">Parts</div>
      <table>
        <thead><tr><th>Part</th><th>Part #</th><th class="text-center">Qty</th><th class="text-right">Cost</th><th class="text-right">Total</th></tr></thead>
        <tbody>${partsRows}</tbody>
      </table>
    </div>`
        : ""
    }

    ${
      materialsRows
        ? `
    <div class="section">
      <div class="section-title">Materials</div>
      <table>
        <thead><tr><th>Material</th><th class="text-center">Qty</th><th class="text-right">Cost</th><th class="text-right">Total</th></tr></thead>
        <tbody>${materialsRows}</tbody>
      </table>
    </div>`
        : ""
    }

    <div class="summary-grid" style="grid-template-columns: repeat(4, 1fr);">
      <div class="summary-card">
        <div class="summary-label">Labor</div>
        <div class="summary-value">${fmt(wo.total_labor_cost)}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">Parts</div>
        <div class="summary-value">${fmt(wo.total_parts_cost)}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">Materials</div>
        <div class="summary-value">${fmt(wo.total_materials_cost)}</div>
      </div>
      <div class="summary-card" style="border-color:#6d28d9;">
        <div class="summary-label">Total</div>
        <div class="summary-value text-brand">${fmt(wo.total_cost)}</div>
      </div>
    </div>
  `;
}
